import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  WorkOrderStatus,
  WorkOrderPriorityCode,
  SettingsLocation,
  CustomerListHeader,
  PaymentMethod,
  AltStockLocation,
  PartListHeader,
  UnitListHeader,
  MiscCharge,
  Zone,
  Country,
  State,
} from "../../models/lookupTypes";
import WorkOrderQueueAPI from "../QueueBoard/api/workOrderApi";
import {
  ApplicationUser,
  ServiceCodes,
  WorkOrdersByTechnician,
  WorkOrder,
  WorkOrderSegmentDetail,
  Filter,
  Unit,
  Customer,
  MeterType,
  MeterTypeList,
  StandardJobCodeType,
  WorkOrdersByStatus,
  WorkOrdersByPriority,
} from "../QueueBoard/models/workOrderTypes";
import { BoardView } from "../QueueBoard/types/boardView";
import { unstable_batchedUpdates } from "react-dom";
import {
  INCLUDE_FINALIZED,
  SELECTED_WO_SALES,
  SELECTED_WO_PRIORITIES,
  USER_IDENTITY,
  VIEW,
  BOARD_ZOOM,
  COLLAPSE_WORK_ORDERS,
} from "../common/constants/filterParamConstants";
import { getCache } from "../common/Utils/cacheUtil";

interface WorkOrderState {
  loading: boolean;
  error: string;
  locations: SettingsLocation[];
  techniciansForFilters: ApplicationUser[];
  technicians: ApplicationUser[];
  woStatuses: WorkOrderStatus[];
  meterTypes: MeterType[];
  woPriorityList: WorkOrderPriorityCode[];
  salesPersons: ApplicationUser[];
  serviceCodes: ServiceCodes[];
  standardJobCodes: StandardJobCodeType[];
  stdJobSources: string[];
  customerListHeadersMyList: CustomerListHeader[];
  customerListHeadersPicklist: CustomerListHeader[];
  paymentMethods: PaymentMethod[];
  altStockLocations: AltStockLocation[];
  partListHeaders: Record<string, PartListHeader[]>; // Keyed by source
  unitListHeadersMyList: UnitListHeader[];
  unitListHeadersPicklist: UnitListHeader[];
  miscCharges: MiscCharge[];
  zones: Zone[];
  /** Billing zones for new customer / customer forms (distinct from board `zones`). */
  customerZones: Zone[];
  countries: Country[];
  states: State[];
  /** Invoice document statuses (InvoiceHeader.Status) for technician calendar filters */
  woDocStatuses: string[];
  filters: Record<string, any>;
  WorkOrdersByStatus: WorkOrdersByStatus[];
  WorkOrdersByTechnician: WorkOrdersByTechnician[];
  WorkOrdersByPriority: WorkOrdersByPriority[];
  currentWorkOrder: WorkOrder | null;
  currentTabIndex: number;
  currentSegmentDetails: WorkOrderSegmentDetail | null;
  currentWorkOrderHistory: WorkOrder | null;
  // Actions
  fetchWorkOrders: (payload: Filter) => Promise<void>;
  resetWorkOrders: () => Promise<void>;
  setLocationLookup: (locations: SettingsLocation[]) => void;
  setTechnicianLookupForFilters: (technicians: ApplicationUser[]) => void;
  setTechnicianLookup: (technicians: ApplicationUser[]) => void;
  setWoStatusLookup: (statuses: WorkOrderStatus[]) => void;
  setWoPriorityListLookup: (priorityList: WorkOrderPriorityCode[]) => void;
  setSalesPersonsLookup: (salesPersons: ApplicationUser[]) => void;
  setStandardJobSourceLookup: (stdJobSources: string[]) => void;
  setServiceCodesLookup: (serviceCodes: ServiceCodes[]) => void;
  setStandardJobCodesLookup: (standardJobCodes: StandardJobCodeType[]) => void;
  setCustomerListHeadersMyList: (headers: CustomerListHeader[]) => void;
  setCustomerListHeadersPicklist: (headers: CustomerListHeader[]) => void;
  setPaymentMethods: (methods: PaymentMethod[]) => void;
  setAltStockLocations: (locations: AltStockLocation[]) => void;
  setPartListHeaders: (source: string, headers: PartListHeader[]) => void;
  setUnitListHeadersMyList: (headers: UnitListHeader[]) => void;
  setUnitListHeadersPicklist: (headers: UnitListHeader[]) => void;
  setMiscCharges: (charges: MiscCharge[]) => void;
  setZones: (zones: Zone[]) => void;
  setCustomerZones: (zones: Zone[]) => void;
  setCountriesLookup: (countries: Country[]) => void;
  setStatesLookup: (states: State[]) => void;
  setWoDocStatusesLookup: (statuses: string[]) => void;
  setFilters: (key: string, value: any) => void;
  setCurrentWorkOrder: (workOrder: WorkOrder) => void;
  setCurrentWorkOrderStatus: (statusIdentity: number) => void;
  clearCurrentWorkOrder: () => void;
  setWorkOrderCurrentTab: (tabIndex: number) => void;
  setWorkOrderUnit: (unit: Unit) => void;
  setCurrentWorkOrderDescription: (description: string) => void;
  setCurrentWorkOrderLocation: (locationIdentity: number) => void;
  setCurrentWorkOrderTech: (technicianIdentity: number) => void;
  setCurrentWorkOrderPriority: (priorityIdentity: number) => void;
  setCurrentWorkOrderSales: (salesPerson: ApplicationUser) => void;
  setWorkOrderCustomer: (customer: Customer) => void;
  setCurrentSegmentDetails: (segmentDetails: WorkOrderSegmentDetail) => void;
  setCurrentWorkOrderHistory: (WorkOrderHistory: WorkOrder) => void;
  resetFilters: () => void;
}

// Helper functions to load from cache on initialization
const getUsername = (): string => {
  return localStorage.getItem("username") || "";
};

const loadCachedLocations = (): SettingsLocation[] => {
  const username = getUsername();
  if (!username) return [];
  const MAX_CACHE_AGE = 1000 * 60 * 60 * 24; // 24 hours
  const cached = getCache<SettingsLocation[]>(
    "settingsLocations",
    username,
    MAX_CACHE_AGE,
  );
  return cached || [];
};

const loadCachedTechnicians = (): ApplicationUser[] => {
  const username = getUsername();
  if (!username) return [];
  const MAX_CACHE_AGE = 1000 * 60 * 60 * 24; // 24 hours
  const cached = getCache<ApplicationUser[]>(
    "technicians",
    username,
    MAX_CACHE_AGE,
  );
  return cached || [];
};

const loadCachedTechniciansForFilters = (): ApplicationUser[] => {
  const username = getUsername();
  if (!username) return [];
  const MAX_CACHE_AGE = 1000 * 60 * 60 * 24; // 24 hours
  const cached = getCache<ApplicationUser[]>(
    "techniciansForFilters",
    username,
    MAX_CACHE_AGE,
  );
  return cached || [];
};

// Create the Zustand store
export const useWorkOrderStore = create<WorkOrderState>()(
  devtools((set, get) => ({
    // Initial state - load from cache on initialization
    loading: false,
    error: "",
    locations: loadCachedLocations(),
    techniciansForFilters: loadCachedTechniciansForFilters(),
    technicians: loadCachedTechnicians(),
    woStatuses: [],
    woPriorityList: [],
    salesPersons: [],
    serviceCodes: [],
    standardJobCodes: [],
    stdJobSources: [],
    customerListHeadersMyList: [],
    customerListHeadersPicklist: [],
    paymentMethods: [],
    altStockLocations: [],
    partListHeaders: {},
    unitListHeadersMyList: [],
    unitListHeadersPicklist: [],
    miscCharges: [],
    zones: [],
    customerZones: [],
    countries: [],
    states: [],
    woDocStatuses: [],
    meterTypes: MeterTypeList,
    filters: (() => {
      const storedUserIdentity = localStorage.getItem(USER_IDENTITY);
      const parsedUserIdentity = storedUserIdentity
        ? parseInt(storedUserIdentity, 10)
        : 0;
      const username = localStorage.getItem("username");
      const currentView =
        username && localStorage.getItem(`${VIEW}_${username}`)
          ? parseInt(localStorage.getItem(`${VIEW}_${username}`)!, 10)
          : 0;
      const salesSelection =
        username && localStorage.getItem(`${SELECTED_WO_SALES}_${username}`)
          ? localStorage.getItem(`${SELECTED_WO_SALES}_${username}`)!.split(",")
          : ["0"];
      const prioritySelection =
        username && localStorage.getItem(`${SELECTED_WO_PRIORITIES}_${username}`)
          ? localStorage.getItem(`${SELECTED_WO_PRIORITIES}_${username}`)!.split(",")
          : ["0"];
      const includeFinalizedStored =
        username &&
        localStorage.getItem(`${INCLUDE_FINALIZED}_${username}`) === "true";
      const boardZoomStored =
        username && localStorage.getItem(`${BOARD_ZOOM}_${username}`)
          ? parseFloat(localStorage.getItem(`${BOARD_ZOOM}_${username}`)!)
          : 1.0;
      const collapseWorkOrdersStored =
        username &&
        localStorage.getItem(`${COLLAPSE_WORK_ORDERS}_${username}`) === "true";
      return {
        userIdentity: parsedUserIdentity,
        view: currentView,
        woSalesPersons: salesSelection,
        woPriorities: prioritySelection,
        includeFinalized: includeFinalizedStored,
        boardZoom: boardZoomStored,
        collapseWorkOrders: collapseWorkOrdersStored,
      };
    })(),
    WorkOrdersByStatus: [],
    WorkOrdersByTechnician: [],
    WorkOrdersByPriority: [],
    currentWorkOrder: null,
    currentTabIndex: 0,
    currentSegmentDetails: null,
    currentWorkOrderHistory: null,
    // Actions
    fetchWorkOrders: async (payload) => {
      // Ensure userIdentity is always present
      const payloadWithUserIdentity = {
        ...payload,
        userIdentity:
          payload.userIdentity ??
          (localStorage.getItem(USER_IDENTITY)
            ? parseInt(localStorage.getItem(USER_IDENTITY)!, 10)
            : 0),
      };

      unstable_batchedUpdates(() => {
        set({ loading: true, error: "" });
      });
      try {
        const response = await WorkOrderQueueAPI.getWorkOrdersBystatus(
          payloadWithUserIdentity,
        );
        if (response === null) {
          throw new Error("No data available");
        }
        const activeView = payloadWithUserIdentity.view ?? BoardView.Status;
        const boardUpdate =
          activeView === BoardView.Technician
            ? { WorkOrdersByTechnician: response.orderByTechnician || [] }
            : activeView === BoardView.Priority
              ? { WorkOrdersByPriority: response.orderByPriority || [] }
              : { WorkOrdersByStatus: response.orderByStatus || [] };

        unstable_batchedUpdates(() => {
          set({
            loading: false,
            ...boardUpdate,
          });
        });
      } catch (error) {
        set({
          error: "Error fetching work orders",
        });
        unstable_batchedUpdates(() => {
          set({
            loading: false,
            error: "Error fetching work orders",
          });
        });
      }
    },
    resetWorkOrders: async () => {
      set({
        WorkOrdersByStatus: [],
        WorkOrdersByTechnician: [],
        WorkOrdersByPriority: [],
      });
    },
    setLocationLookup: (locations) => set({ locations }),
    setTechnicianLookupForFilters: (technicians) =>
      set({ techniciansForFilters: technicians }),
    setTechnicianLookup: (technicians) => set({ technicians }),
    setWoStatusLookup: (statuses) => set({ woStatuses: statuses }),
    setWoPriorityListLookup: (priorityList) =>
      set({ woPriorityList: priorityList }),
    setSalesPersonsLookup: (salesPersons) => set({ salesPersons }),
    setServiceCodesLookup: (serviceCodes) => set({ serviceCodes }),
    setStandardJobCodesLookup: (standardJobCodes) => set({ standardJobCodes }),
    setStandardJobSourceLookup: (stdJobSources) => set({ stdJobSources }),
    setCustomerListHeadersMyList: (headers) =>
      set({ customerListHeadersMyList: headers }),
    setCustomerListHeadersPicklist: (headers) =>
      set({ customerListHeadersPicklist: headers }),
    setPaymentMethods: (methods) => set({ paymentMethods: methods }),
    setAltStockLocations: (locations) => set({ altStockLocations: locations }),
    setPartListHeaders: (source, headers) =>
      set((state) => ({
        partListHeaders: { ...state.partListHeaders, [source]: headers },
      })),
    setUnitListHeadersMyList: (headers) =>
      set({ unitListHeadersMyList: headers }),
    setUnitListHeadersPicklist: (headers) =>
      set({ unitListHeadersPicklist: headers }),
    setMiscCharges: (charges) => set({ miscCharges: charges }),
    setZones: (zones) => set({ zones }),
    setCustomerZones: (customerZones) => set({ customerZones }),
    setCountriesLookup: (countries) => set({ countries }),
    setStatesLookup: (states) => set({ states }),
    setWoDocStatusesLookup: (statuses) => set({ woDocStatuses: statuses }),
    setFilters: (key, value) =>
      set((state) => ({ filters: { ...state.filters, [key]: value } })),
    setCurrentWorkOrder: (workOrder) => set({ currentWorkOrder: workOrder }),
    setCurrentWorkOrderStatus: (statusIdentity) => {
      set((state) => {
        if (state.currentWorkOrder) {
          state.currentWorkOrder.WorkOrderStatusIdentity = statusIdentity;
        }
        return state;
      });
    },
    clearCurrentWorkOrder: () => set({ currentWorkOrder: null }),
    setWorkOrderCurrentTab: (tabIndex) => set({ currentTabIndex: tabIndex }),
    setWorkOrderUnit: (unit) => {
      set((state) => {
        if (state.currentWorkOrder) {
          state.currentWorkOrder.stockNumber = unit.unitNumber;
        }
        return state;
      });
    },
    setCurrentWorkOrderDescription: (description) => {
      set((state) => {
        if (state.currentWorkOrder) {
          state.currentWorkOrder.description = description;
        }
        return state;
      });
    },
    setCurrentWorkOrderLocation: (locationIdentity) => {
      set((state) => {
        if (state.currentWorkOrder) {
          state.currentWorkOrder.locationIdentity = locationIdentity;
        }
        return state;
      });
    },
    setCurrentWorkOrderTech: (technicianIdentity) => {
      set((state) => {
        if (state.currentWorkOrder) {
          state.currentWorkOrder.technicianIdentity = technicianIdentity;
        }
        return state;
      });
    },
    setCurrentWorkOrderPriority: (priorityIdentity) => {
      set((state) => {
        if (state.currentWorkOrder) {
          state.currentWorkOrder.woPriorityIdentity = priorityIdentity;
        }
        return state;
      });
    },
    setCurrentWorkOrderSales: (salesPerson) => {
      set((state) => {
        if (state.currentWorkOrder) {
          state.currentWorkOrder.salesPersonIdentity = salesPerson.identity;
          state.currentWorkOrder.salesPersonName = salesPerson.userName;
        }
        return state;
      });
    },
    setWorkOrderCustomer: (customer) => {
      set((state) => {
        if (state.currentWorkOrder) {
          state.currentWorkOrder.customerName = customer.name;
          state.currentWorkOrder.customerIdentity = customer.identity;
        }
        return state;
      });
    },
    setCurrentSegmentDetails: (segmentDetails) =>
      set({ currentSegmentDetails: segmentDetails }),

    setCurrentWorkOrderHistory: (WorkOrderHistory) =>
      set({ currentWorkOrderHistory: WorkOrderHistory }),
    resetFilters: () =>
      set((state) => ({
        filters: {
          view: 0,
          userIdentity: state.filters.userIdentity, // Keep userIdentity
          woStatuses: ["0"],
          woTechs: ["0"],
          woLocations: ["0"],
          woSalesPersons: ["0"],
          woPriorities: ["0"],
          woCustomers: [],
          includeFinalized: false,
          customerIdentity: undefined,
          searchString: "",
          workOrderCreationStartDate: undefined,
          workOrderCreationEndDate: undefined,
          workOrderScheduleFromDate: undefined,
          workOrderScheduleToDate: undefined,
          workOrderDeliveryStartDate: undefined,
          workOrderDeliveryEndDate: undefined,
          workOrderPickupStartDate: undefined,
          workOrderPickupEndDate: undefined,
          filterClockedHours: false,
          filterOverClockedHours: false,
        },
      })),
  })),
);

export const useBoardLoading = () => useWorkOrderStore((s) => s.loading);
export const useBoardFilters = () => useWorkOrderStore((s) => s.filters);
export const useStatusBoardData = () =>
  useWorkOrderStore((s) => s.WorkOrdersByStatus);
export const useTechBoardData = () =>
  useWorkOrderStore((s) => s.WorkOrdersByTechnician);
export const usePriorityBoardData = () =>
  useWorkOrderStore((s) => s.WorkOrdersByPriority);
